import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { fetchUserDocuments } from '../../api/profile';
import LoadingSpinner from '../common/Loader';
import DocumentRequests from './DocumentRequests';
import { 
  DocumentTextIcon, 
  DocumentIcon, 
  PhotoIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const MyDocuments = () => {
  const { user } = useContext(AuthContext);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const docs = await fetchUserDocuments(user.user_id, user.access_token);
        setDocuments(docs);
      } catch (err) {
        // Suppress error if 404 Document Requests not found or "Failed to load document requests"
        if (
          (err.status === 404 && err.message === 'Document Requests not found') ||
          err.message === 'Failed to load document requests'
        ) {
          setError(null);
          setDocuments([]);
        } else {
          setError(err.message || 'Failed to load documents');
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user.user_id, user.access_token]);

  // Helper function to determine file type from extension
  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    switch(ext) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return 'image';
      case 'pdf':
        return 'pdf';
      default:
        return 'other';
    }
  };

  // Helper function to get file type label
  const getFileTypeLabel = (filename) => {
    const ext = filename.split('.').pop().toUpperCase();
    return `${ext} file`;
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Helper function to get icon for file type
  const getFileIcon = (filename) => {
    const type = getFileType(filename);
    switch(type) {
      case 'image':
        return <PhotoIcon className="h-5 w-5 text-primary" />;
      case 'pdf':
        return <DocumentTextIcon className="h-5 w-5 text-red-500" />;
      default:
        return <DocumentIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  if (loading) return <LoadingSpinner />;

  if (error) return null;

  return (
    <div className="p-4 lg:p-6">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">My Documents</h1>

      {/* Document Requests Section */}
      <DocumentRequests />

      {/* Uploaded Documents Section */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">My Uploaded Documents</h2>
        
        {documents.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <p className="text-gray-500">No documents found</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {documents.map((doc, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            {getFileIcon(doc.filename)}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                              {doc.filename}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {getFileTypeLabel(doc.filename)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatFileSize(doc.size)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(doc.lastModified).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setPreviewDoc(doc)}
                            className="text-primary hover:text-primary flex items-center"
                          >
                            <EyeIcon className="h-4 w-4 mr-1" /> Preview
                          </button>
                          <a
                            href={doc.url}
                            download={doc.filename}
                            className="text-primary hover:text-primary flex items-center"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4 mr-1" /> Download
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
              {documents.map((doc, index) => (
                <div key={index} className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 pt-1">
                        {getFileIcon(doc.filename)}
                      </div>
                      <div className="ml-4 flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {doc.filename}
                        </h3>
                        <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-gray-500">
                          <span>{getFileTypeLabel(doc.filename)}</span>
                          <span>{formatFileSize(doc.size)}</span>
                          <span>{new Date(doc.lastModified).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="ml-2 flex flex-col space-y-1">
                        <button
                          onClick={() => setPreviewDoc(doc)}
                          className="text-primary hover:text-primary p-1"
                          title="Preview"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <a
                          href={doc.url}
                          download={doc.filename}
                          className="text-primary hover:text-primary p-1"
                          title="Download"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Preview Modal */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium text-gray-900 truncate">
                {previewDoc.filename}
              </h3>
              <button 
                onClick={() => setPreviewDoc(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 flex justify-center items-center">
              {getFileType(previewDoc.filename) === 'image' ? (
                <img 
                  src={previewDoc.url} 
                  alt={previewDoc.filename}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              ) : getFileType(previewDoc.filename) === 'pdf' ? (
                <iframe 
                  src={previewDoc.url} 
                  title={previewDoc.filename}
                  className="w-full h-[70vh] border"
                />
              ) : (
                <div className="text-center py-10">
                  <p className="text-gray-500 mb-4">Preview not available for this file type</p>
                  <a
                    href={previewDoc.url}
                    download={previewDoc.filename}
                    className="text-primary hover:text-primary font-medium inline-flex items-center"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5 mr-1" />
                    Download File
                  </a>
                </div>
              )}
            </div>
            <div className="p-4 border-t flex justify-end">
              <a
                href={previewDoc.url}
                download={previewDoc.filename}
                className="bg-primary hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center"
              >
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                Download
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyDocuments;